import React, { useState, useMemo } from "react"
import { Box, Paper, Typography, Stack, Grid, TextField, InputAdornment, Chip, Avatar } from "@mui/material"
import ListIcon from "@mui/icons-material/List"
import BuildIcon from "@mui/icons-material/Build"
import SearchIcon from "@mui/icons-material/Search"
import CalculateIcon from "@mui/icons-material/Calculate"
import InventoryIcon from "@mui/icons-material/Inventory"
import { useTranslation } from "react-i18next"
import { useVisualEditor } from "../VisualEditorContext"
import StepCard from "./StepCard"
import DraggableMaterial from "./DraggableMaterial"

const StepsTab = () => {
  const { t } = useTranslation()
  const { steps, materialsList } = useVisualEditor()
  const [search, setSearch] = useState("")

  // Filter materials to show only MATERIAL and PURCHASED-MATERIAL
  const availableMaterials = useMemo(() => {
    const filtered = materialsList.filter(
      (mat) => mat.usageType === "MATERIAL" || mat.usageType === "PURCHASED-MATERIAL"
    )
    if (!search) return filtered
    const searchRegex = new RegExp(search, "i")
    return filtered.filter(
      (mat) => searchRegex.test(mat.name) || searchRegex.test(mat.description || "")
    )
  }, [materialsList, search])

  // Calculate total cost across all steps
  const totalCost = useMemo(() => {
    return steps.reduce((total, step) => {
      const stepCost = (step.materials || []).reduce((sum, mat) => {
        const qty = parseFloat(mat.quantity) || 0
        const cost = parseFloat(mat.costprice) || 0
        return sum + (qty * cost)
      }, 0)
      return total + stepCost
    }, 0)
  }, [steps])

  // Count total materials
  const totalMaterials = useMemo(() => {
    return steps.reduce((total, step) => {
      return total + (step.materials?.length || 0)
    }, 0)
  }, [steps])

  return (
    <Grid container spacing={2}>
      {/* Left Panel - Steps List (70%) */}
      <Grid item xs={12} md={8}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: "1px solid #e2e8f0",
            minHeight: 500,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <ListIcon sx={{ color: "#3b82f6" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                {t("Production Steps")}
              </Typography>
              <Typography variant="caption" color="#64748b">
                {t("Drag to reorder steps, drag materials from right panel")}
              </Typography>
            </Box>
            {steps.length > 0 && (
              <Chip
                size="small"
                label={`${steps.length} ${steps.length === 1 ? t("step") : t("steps")}`}
                sx={{ bgcolor: "#f1f5f9", color: "#64748b" }}
              />
            )}
          </Stack>

          {steps.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                color: "#94a3b8",
              }}
            >
              <BuildIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
              <Typography variant="body1" fontWeight={500} color="#64748b">
                {t("No production steps defined")}
              </Typography>
              <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
                {t("Go to the Sections tab to add steps by dragging sections")}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {steps.map((step, index) => (
                <StepCard key={index} step={step} index={index} />
              ))}
            </Stack>
          )}
        </Paper>

        {/* Cost Summary */}
        {steps.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
            }}
          >
            <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 36, height: 36 }}>
                  <CalculateIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                    {t("Cost Summary")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {totalMaterials} {t("materials")} {t("across")} {steps.length} {t("steps")}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="caption" color="#64748b">
                    {t("Total Cost")}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="#16a34a">
                    €{totalCost.toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Grid>

      {/* Right Panel - Materials Palette (30%) */}
      <Grid item xs={12} md={4}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: "1px solid #e2e8f0",
            position: "sticky",
            top: 80,
            maxHeight: "calc(100vh - 120px)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <InventoryIcon sx={{ color: "#8b5cf6" }} />
            <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
              {t("Materials")}
            </Typography>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder={t("Search materials...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="caption" color="#64748b" sx={{ mb: 1.5, display: "block" }}>
            {t("Drag materials to steps on the left")}
          </Typography>

          <Box sx={{ flex: 1, overflow: "auto" }}>
            <Stack spacing={1}>
              {availableMaterials.map((material, index) => (
                <DraggableMaterial key={index} material={material} />
              ))}

              {availableMaterials.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4, color: "#94a3b8" }}>
                  <Typography variant="body2">
                    {materialsList.length === 0 ? t("No materials available") : t("No matching materials")}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Materials Legend */}
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e2e8f0" }}>
            <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ mb: 1, display: "block" }}>
              {t("Legend")}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                size="small"
                icon={<BuildIcon sx={{ fontSize: 12 }} />}
                label={t("Material")}
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  bgcolor: "#dbeafe",
                  color: "#1d4ed8",
                  "& .MuiChip-icon": { color: "#1d4ed8" },
                }}
              />
              <Chip
                size="small"
                icon={<InventoryIcon sx={{ fontSize: 12 }} />}
                label={t("Purchased")}
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  bgcolor: "#fef3c7",
                  color: "#d97706",
                  "& .MuiChip-icon": { color: "#d97706" },
                }}
              />
            </Stack>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  )
}

export default StepsTab
